import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { useSnackbar } from '@Providers/useSnackbar';
import { useTypedSelector } from '@Features/store';
import { fetchAssets } from '@Features/settings/settingsSlice';
import { FlexLayout, GridLayout } from '@Styled/utilities';
import AssetSlotCard from './AssetSlotCard';
import AssetUploadButton from './AssetUploadButton';
import AddAssetDialog from './AddAssetDialog';
import { deleteAsset, upsertAsset } from './assets.requester';
import { SUBJECT_ICON_PREFIX } from '@Components/UI/Inputs/SubjectIconPicker/SubjectIconPicker';

const FIXED_SLOTS: Array<{
	key: string;
	title: string;
	hint?: string;
	cropRatio?: number;
}> = [
	{
		key: 'logo',
		title: 'Logo',
		hint: 'Shown across the portals. Transparent PNG/WebP around 640×160 px — it is contained into 160×42 px in the student header and ~130×35 px in the sidebar.',
	},
	{
		key: 'logo_small',
		title: 'Small Logo',
		hint: 'Collapsed sidebar mark. Square, transparent, around 160×160 px.',
	},
	{ key: 'favicon', title: 'Favicon', hint: 'PNG, 32×32 or 64×64' },
	{
		key: 'landing_main',
		title: 'Landing Image',
		hint: 'Student landing hero. The cropper locks 16:5 and saves WebP — upload at least 1920×600 px, ideally 2560×800. The whole crop is always shown: full-bleed on desktop, full-width in a short band on phones (unless a mobile landing image is set). The optional settings headline overlays the right side in both languages, so keep that side quiet.',
		cropRatio: 16 / 5,
	},
	{
		key: 'landing_main_mobile',
		title: 'Landing Image (Mobile)',
		hint: 'Phone version of the landing hero. The cropper locks 4:3 — upload at least 1080×810 px. Phones show the whole crop in a taller band; leave it unset and phones show the whole desktop hero in a shorter band.',
		cropRatio: 4 / 3,
	},
];

/** Core slots every portal reads. Clearing one reverts to the bundled default. */
const isCoreSlot = (key: string | null) =>
	!!key && FIXED_SLOTS.some((slot) => slot.key === key);

const AssetsTab = () => {
	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const { assets } = useTypedSelector((state) => state.settings);
	const [addOpen, setAddOpen] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<string | null>(null);

	useEffect(() => {
		dispatch(fetchAssets());
	}, []);

	const customKeys = useMemo(
		() =>
			Object.keys(assets)
				.filter(
					(key) =>
						!FIXED_SLOTS.some((slot) => slot.key === key) &&
						!key.startsWith(SUBJECT_ICON_PREFIX)
				)
				.sort(),
		[assets]
	);

	const subjectIcons = useMemo(
		() =>
			Object.keys(assets)
				.filter((key) => key.startsWith(SUBJECT_ICON_PREFIX))
				.sort()
				.map((key) => ({
					key,
					url: assets[key],
					label: key.slice(SUBJECT_ICON_PREFIX.length).replace(/_/g, ' '),
				})),
		[assets]
	);

	const saveAsset = async (key: string, value: string) => {
		try {
			await upsertAsset(key, value);
			displaySnackbar('success', `Asset "${key}" saved successfully`);
			setAddOpen(false);
			dispatch(fetchAssets());
		} catch (error: any) {
			const message =
				error?.response?.data?.errors?.[0]?.message ??
				error?.response?.data?.message ??
				`Unable to save asset "${key}"`;
			displaySnackbar('error', message);
		}
	};

	const onConfirmDelete = async (close: boolean, confirm?: boolean) => {
		const key = pendingDelete;
		setPendingDelete(null);
		if (!confirm || !key) return;
		try {
			await deleteAsset(key);
			displaySnackbar(
				'success',
				isCoreSlot(key)
					? `Asset "${key}" cleared — the built-in default is back in use`
					: `Asset "${key}" deleted successfully`
			);
			dispatch(fetchAssets());
		} catch (error: any) {
			const message =
				error?.response?.data?.message ??
				`Unable to ${isCoreSlot(key) ? 'clear' : 'delete'} asset "${key}"`;
			displaySnackbar('error', message);
		}
	};

	return (
		<Container>
			<SlotsLayout>
				{FIXED_SLOTS.map((slot) => (
					<AssetSlotCard
						key={slot.key}
						title={slot.title}
						hint={slot.hint}
						cropRatio={slot.cropRatio}
						value={assets[slot.key]}
						onUploaded={(uri) => saveAsset(slot.key, uri)}
						onClear={() => setPendingDelete(slot.key)}
					/>
				))}
			</SlotsLayout>

			<CustomSection>
				<FlexLayout justifyContent="space-between" alignItems="center">
					<Typography variant="h6">Subject Icons</Typography>
				</FlexLayout>
				{subjectIcons.length === 0 ? (
					<Typography variant="body2" color="textSecondary">
						No subject icons yet — add them here or from the Subject form
					</Typography>
				) : (
					<IconsGrid>
						{subjectIcons.map((icon) => (
							<IconTile key={icon.key}>
								<IconThumb src={icon.url} alt={icon.label} />
								<IconLabel title={icon.key}>{icon.label}</IconLabel>
								<IconActions>
									<AssetUploadButton
										iconOnly
										label="Replace"
										onUploaded={(uri) => saveAsset(icon.key, uri)}
									/>
									<Tooltip title="Delete">
										<IconButton
											size="small"
											onClick={() => setPendingDelete(icon.key)}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</IconActions>
							</IconTile>
						))}
					</IconsGrid>
				)}
			</CustomSection>

			<CustomSection>
				<FlexLayout justifyContent="space-between" alignItems="center">
					<Typography variant="h6">Custom Assets</Typography>
					<EdButton
						variant="contained"
						edcolor="purple"
						startIcon={<AddIcon />}
						onClick={() => setAddOpen(true)}
					>
						Add Asset
					</EdButton>
				</FlexLayout>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>KEY</TableCell>
							<TableCell>VALUE</TableCell>
							<TableCell align="right">ACTIONS</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{customKeys.length === 0 && (
							<TableRow>
								<TableCell colSpan={3}>
									<Typography variant="body2" color="textSecondary">
										No custom assets yet
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{customKeys.map((key) => (
							<TableRow key={key}>
								<TableCell>{key}</TableCell>
								<TableCell>
									<ValueCell alignItems="center" gridGap="0.75rem">
										<ValueThumb src={assets[key]} alt={key} />
										<ValueUrl variant="caption">{assets[key]}</ValueUrl>
									</ValueCell>
								</TableCell>
								<TableCell align="right">
									<ReplaceButtonWrapper>
										<AssetUploadButton
											label="Replace"
											onUploaded={(uri) => saveAsset(key, uri)}
										/>
									</ReplaceButtonWrapper>
									<Tooltip title="Delete">
										<IconButton
											size="small"
											onClick={() => setPendingDelete(key)}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</CustomSection>

			<AddAssetDialog
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onSubmit={saveAsset}
			/>

			<EdConfirmModal
				open={!!pendingDelete}
				handleClose={onConfirmDelete}
				text={
					// eslint-disable-next-line no-nested-ternary
					pendingDelete
						? isCoreSlot(pendingDelete)
							? `Clear "${pendingDelete}"? The portals fall back to their built-in default image until a new one is uploaded.`
							: `Are you sure you want to delete asset "${pendingDelete}"?`
						: ''
				}
			/>
		</Container>
	);
};

export default AssetsTab;

const Container = styled.div`
	padding: 2rem;
`;

const SlotsLayout = styled(GridLayout)`
	grid-template-columns: 1fr 1fr;
	gap: 1.5rem;
	align-items: start;

	@media (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;

const CustomSection = styled(Paper)`
	margin-top: 1.5rem;
	padding: 1rem 1.5rem;
`;

const ValueCell = styled(FlexLayout)``;

const ValueThumb = styled.img`
	width: 2.5rem;
	height: 2.5rem;
	object-fit: contain;
	background-color: ${(props) => props.theme.palette.grey['100']};
	border-radius: 4px;
`;

const ValueUrl = styled(Typography)`
	max-width: 20rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	display: inline-block;
`;

const ReplaceButtonWrapper = styled.span`
	margin-right: 0.5rem;
`;

const IconsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, 6.5rem);
	gap: 0.75rem;
	margin-top: 0.75rem;
`;

const IconTile = styled.div`
	border: 1px solid ${(props) => props.theme.palette.grey['300']};
	border-radius: 8px;
	padding: 0.5rem 0.25rem;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.25rem;
	overflow: hidden;
`;

const IconThumb = styled.img`
	width: 2rem;
	height: 2rem;
	object-fit: contain;
`;

const IconActions = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.125rem;
`;

const IconLabel = styled(Typography).attrs({ variant: 'caption' })`
	text-transform: capitalize;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;
